import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import MainContent from '../components/MainContent';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import {
  AUDIENCE_LABELS,
  CARD,
  SMS_BASE,
  SmsUnavailable,
  StatusChip,
  formatDateTime,
  isActive,
} from '../components/sms/smsShared';

const PAGE_SIZE = 20;

const truncate = (text, max = 60) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text;

function SmsCampaigns() {
  const navigate = useNavigate();
  const [skip, setSkip] = useState(0);

  const { data, isLoading, error, refetch } = useQuery(
    ['sms-campaigns', skip],
    async () =>
      (
        await axiosInstancev2.get(SMS_BASE, {
          params: { limit: PAGE_SIZE, skip },
        })
      ).data,
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      // Keep counts moving while anything on this page is still sending.
      refetchInterval: (result) =>
        result?.campaigns?.some((c) => isActive(c.status)) ? 10000 : false,
    }
  );

  const status = error?.response?.status;
  const campaigns = data?.campaigns ?? [];
  const total = data?.total ?? 0;
  const canCreate = !error || status >= 500;

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <div
          className={`${CARD} px-5 py-4 flex flex-wrap items-center justify-between gap-4`}
        >
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Bulk SMS</h1>
            <p className="text-xs text-gray-500">
              Personalised SMS to cohorts, alumni, graduates or a custom list.
              Kenyan mobile numbers only.
            </p>
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={() => navigate('/sms/new')}
              className="flex items-center gap-1 bg-claret-500 hover:bg-claret-400 px-4 py-2 rounded-md text-white text-sm"
            >
              <AddIcon fontSize="small" />
              New SMS
            </button>
          )}
        </div>

        {(() => {
          if (isLoading) {
            return (
              <div
                className={`${CARD} h-64 animate-pulse`}
                aria-label="Loading"
              />
            );
          }
          if (error) {
            return <SmsUnavailable status={status} onRetry={refetch} />;
          }
          if (total === 0) {
            return (
              <div className={`${CARD} p-10 text-center space-y-2`}>
                <p className="text-sm text-gray-900">No SMS campaigns yet</p>
                <p className="text-xs text-gray-500">
                  Campaigns you send will appear here with their delivery
                  progress.
                </p>
              </div>
            );
          }
          return (
            <section className={`${CARD} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-200">
                      <th className="py-2 pl-5 pr-2 font-medium">Created</th>
                      <th className="py-2 px-2 font-medium">Created by</th>
                      <th className="py-2 px-2 font-medium">Audience</th>
                      <th className="py-2 px-2 font-medium">Message</th>
                      <th className="py-2 px-2 font-medium text-right">
                        Recipients
                      </th>
                      <th className="py-2 px-2 font-medium">Status</th>
                      <th className="py-2 pl-2 pr-5 font-medium text-right">
                        Sent / Failed / Unknown
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((campaign) => (
                      <tr
                        key={campaign.id}
                        onClick={() => navigate(`/sms/${campaign.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            navigate(`/sms/${campaign.id}`);
                          }
                        }}
                        tabIndex={0}
                        className="border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
                      >
                        <td className="py-3 pl-5 pr-2 text-gray-700 whitespace-nowrap text-xs">
                          {formatDateTime(campaign.created_at)}
                        </td>
                        <td className="py-3 px-2 text-gray-700 text-xs">
                          {campaign.created_by}
                        </td>
                        <td className="py-3 px-2 text-gray-700 text-xs whitespace-nowrap">
                          {AUDIENCE_LABELS[campaign.audience_type] ??
                            campaign.audience_type}
                        </td>
                        <td
                          className="py-3 px-2 text-gray-900"
                          title={campaign.message}
                        >
                          {truncate(campaign.message)}
                        </td>
                        <td className="py-3 px-2 text-right tabular-nums text-gray-700">
                          {(campaign.recipient_count ?? 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-2">
                          <StatusChip status={campaign.status} />
                        </td>
                        <td className="py-3 pl-2 pr-5 text-right tabular-nums text-xs whitespace-nowrap">
                          <span className="text-green-700">
                            {(campaign.counts?.sent ?? 0).toLocaleString()}
                          </span>
                          <span className="text-gray-300"> / </span>
                          <span className="text-red-700">
                            {(campaign.counts?.failed ?? 0).toLocaleString()}
                          </span>
                          <span className="text-gray-300"> / </span>
                          <span className="text-amber-700">
                            {(campaign.counts?.unknown ?? 0).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-100 text-xs text-gray-600">
                <span>
                  {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of{' '}
                  {total.toLocaleString()}
                </span>
                <button
                  type="button"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(skip - PAGE_SIZE, 0))}
                  className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={skip + PAGE_SIZE >= total}
                  onClick={() => setSkip(skip + PAGE_SIZE)}
                  className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </section>
          );
        })()}
      </div>
    </MainContent>
  );
}

export default SmsCampaigns;
